import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiDollarSign, 
  FiSearch, 
  FiFilter, 
  FiCalendar, 
  FiCheck, 
  FiX, 
  FiEye, 
  FiDownload, 
  FiClock, 
  FiPackage,
  FiCheckCircle
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import { api } from '../../api/api';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const REFUND_STATUSES = {
  APPROVED: 'approved',
  REJECTED: 'rejected',
  COMPLETED: 'completed'
};

const getStatusColor = (status) => {
  const colors = {
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    completed: 'bg-green-100 text-green-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const OrderHistoryTimeline = ({ orders }) => {
  return (
    <div className="space-y-4">
      {orders.map((order, index) => (
        <div key={order.id} className="relative pl-8 pb-4">
          {/* Timeline connector */}
          {index !== orders.length - 1 && (
            <div className="absolute left-3 top-6 bottom-0 w-0.5 bg-gray-200"></div>
          )}
          
          {/* Timeline dot */}
          <div className={`absolute left-2 top-2 w-3 h-3 rounded-full ${
            order.status === 'delivered' ? 'bg-green-500' :
            order.status === 'cancelled' ? 'bg-red-500' : 'bg-blue-500'
          }`}></div>
          
          {/* Order content */}
          <div className="bg-white rounded-lg border p-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h4 className="font-medium text-gray-900">Order #{order.order_number}</h4>
                <p className="text-sm text-gray-500">
                  {new Date(order.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${
                order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
            </div>
            
            <div className="space-y-2">
              {order.items.map((item) => (
                <div key={item.id} className="flex items-center gap-3 text-sm">
                  <div className="w-12 h-12 rounded-md bg-gray-100 overflow-hidden">
                    <img 
                      src={item.image} 
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{item.name}</p>
                    <p className="text-gray-500">
                      Qty: {item.quantity} × ₹{item.price}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Payment Status</p>
                <p className={`font-medium ${
                  order.payment_status === 'paid' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {order.payment_status.toUpperCase()}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Total Amount</p>
                <p className="font-medium">₹{order.total_amount}</p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const RefundRequestDetails = ({ request, onClose, onRefresh }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(request.status);

  const statusOptions = [
    { value: 'approved', label: 'Approved', color: 'bg-green-100 text-green-800' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
    { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800' }
  ];

  const handleStatusUpdate = async () => {
    if (selectedStatus === request.status) {
      return;
    }

    try {
      setIsUpdating(true);
      const loadingToast = toast.loading('Updating refund request...');

      await api.updateRefundStatus(request.id, selectedStatus);

      toast.dismiss(loadingToast);
      toast.success('Refund request updated successfully');
      
      // Update the local state
      request.status = selectedStatus;
      
      // Refresh the refund requests list using the passed function
      await onRefresh();
      
      // Close the modal if it exists
      if (typeof onClose === 'function') {
        onClose();
      }
    } catch (error) {
      console.error('Error updating refund status:', error);
      toast.error(
        error.message === 'No authentication token found' 
          ? 'Please log in again to continue' 
          : error.message || 'Failed to update refund status'
      );
      
      if (error.message === 'Unauthorized - Please log in again' || 
          error.message === 'No authentication token found') {
        window.location.href = '/login';
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Refund Request Details</h2>
              <p className="mt-1 text-sm text-gray-500">
                Request ID: #{request.id}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <FiX className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Customer Information */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-3">Customer Information</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="font-medium">{request.customer_details.full_name}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-medium">{request.customer_details.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="font-medium">{request.customer_details.phone_number}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Order Information */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-3">Order Information</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Order Number</p>
                  <p className="font-medium">#{request.order_details.order_number}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Order Date</p>
                  <p className="font-medium">
                    {new Date(request.order_details.order_date).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Order Status</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                    request.order_details.order_status === 'completed' ? 'bg-green-100 text-green-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {request.order_details.order_status.toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total Amount</p>
                  <p className="font-medium">₹{request.order_details.total_amount.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Details */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-3">Payment Details</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Payment Status</p>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                    request.order_details.payment_details.status === 'completed' ? 'bg-green-100 text-green-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {request.order_details.payment_details.status.toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Razorpay Payment ID</p>
                  <p className="font-medium">{request.order_details.payment_details.razorpay_payment_id}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Razorpay Order ID</p>
                  <p className="font-medium">{request.order_details.payment_details.razorpay_order_id}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Invoice</p>
                  <a 
                    href={request.order_details.payment_details.invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
                  >
                    <FiDownload className="w-4 h-4" />
                    Download Invoice
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-3">Shipping Address</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="font-medium">{request.order_details.shipping_address.full_name}</p>
              <p className="text-sm text-gray-600">
                {request.order_details.shipping_address.street_address_1}
                {request.order_details.shipping_address.street_address_2 && `, ${request.order_details.shipping_address.street_address_2}`}
              </p>
              <p className="text-sm text-gray-600">
                {request.order_details.shipping_address.city}, {request.order_details.shipping_address.state}
              </p>
              <p className="text-sm text-gray-600">
                {request.order_details.shipping_address.country} - {request.order_details.shipping_address.pincode}
              </p>
              <p className="text-sm text-gray-600">
                Phone: {request.order_details.shipping_address.phone}
              </p>
            </div>
          </div>

          {/* Order Items */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-3">Order Items</h3>
            <div className="space-y-3">
              {request.order_details.items.map((item) => (
                <div key={item.id} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{item.product_name}</p>
                      <div className="mt-2 text-sm text-gray-500 space-y-1">
                        <p>Size: {item.variant.size}</p>
                        <p>Color: {item.variant.color}</p>
                        <p>SKU: {item.variant.sku}</p>
                        <p>Quantity: {item.quantity}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Price: ₹{item.price.toLocaleString()}</p>
                      <p className="font-medium">Total: ₹{item.total.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Enhanced Refund Request Details Section */}
          <div className="p-6 space-y-6">
            {/* Current Status Display */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-medium text-gray-500">Current Status</h3>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  statusOptions.find(opt => opt.value === request.status)?.color
                }`}>
                  {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                </span>
              </div>

              {/* Status Update Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Update Status
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    disabled={isUpdating}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={handleStatusUpdate}
                    disabled={isUpdating || selectedStatus === request.status}
                    className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                      ${isUpdating || selectedStatus === request.status ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} 
                      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                  >
                    {isUpdating ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Updating...
                      </>
                    ) : (
                      <>
                        <FiCheck className="mr-2 -ml-1 h-4 w-4" />
                        Update Request
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Request Timeline */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-3">Request Timeline</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                      <FiClock className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-900">Request Created</p>
                    <p className="text-sm text-gray-500">
                      {new Date(request.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                {request.status_history?.map((history, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <FiInfo className="h-5 w-5 text-blue-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-900">
                        Status updated to {history.status}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(history.updated_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Original Request Details */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-500 mb-3">Original Request Details</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500">Request Date</p>
                  <p className="font-medium">{new Date(request.created_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Reason</p>
                  <p className="font-medium">{request.reason}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Description</p>
                  <p className="font-medium">{request.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const StatusUpdateModal = ({ isOpen, onClose, onUpdate, currentStatus }) => {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const statusOptions = [
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'completed', label: 'Completed' }
  ];

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await onUpdate(selectedStatus);
      onClose();
      toast.success('Status updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">Update Refund Status</h2>
        
        <div className="space-y-4">
          {statusOptions.map((option) => (
            <label
              key={option.value}
              className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
            >
              <input
                type="radio"
                name="status"
                value={option.value}
                checked={selectedStatus === option.value}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="ml-3">{option.label}</span>
            </label>
          ))}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center"
          >
            {isSubmitting ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                Updating...
              </>
            ) : (
              <>
                <FiCheck className="mr-2" />
                Update Status
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const RefundRequests = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: { start: null, end: null }
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchRefundRequests();
  }, []);

  const fetchRefundRequests = async () => {
    setIsLoading(true);
    try {
      const data = await api.getRefundRequests();
      
      // Transform the data to include all necessary information
      const transformedRequests = data.map(request => ({
        id: request.id,
        reason: request.reason,
        description: request.description,
        status: request.status,
        created_at: request.created_at,
        customer_details: {
          id: request.customer_details.id,
          full_name: request.customer_details.full_name,
          email: request.customer_details.email,
          phone_number: request.customer_details.phone_number
        },
        order_details: {
          order_id: request.order_details.order_id,
          order_number: request.order_details.order_number,
          total_amount: parseFloat(request.order_details.total_amount),
          order_date: request.order_details.order_date,
          order_status: request.order_details.order_status,
          payment_details: {
            status: request.order_details.payment_details.status,
            razorpay_order_id: request.order_details.payment_details.razorpay_order_id,
            razorpay_payment_id: request.order_details.payment_details.razorpay_payment_id,
            invoice_url: request.order_details.payment_details.invoice_url
          },
          shipping_address: {
            ...request.order_details.shipping_address,
            full_name: `${request.order_details.shipping_address.first_name} ${request.order_details.shipping_address.last_name}`
          },
          items: request.order_details.items.map(item => ({
            id: item.id,
            product_name: item.product_name,
            variant: item.variant_details,
            quantity: item.quantity,
            price: parseFloat(item.price),
            total: parseFloat(item.total)
          }))
        }
      }));

      // Sort by created_at date, latest first
      const sortedRequests = transformedRequests.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );

      setRequests(sortedRequests);
      setFilteredRequests(sortedRequests);
    } catch (error) {
      console.error('Error fetching refund requests:', error);
      toast.error(error.message || 'Failed to load refund requests');
      setRequests([]);
      setFilteredRequests([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      // Show loading toast
      const loadingToast = toast.loading('Updating refund status...');
      
      await api.updateRefundStatus(requestId, newStatus);
      
      // Update local state
      setRequests(prevRequests =>
        prevRequests.map(request =>
          request.id === requestId
            ? { ...request, status: newStatus }
            : request
        )
      );

      // Update filtered requests as well
      setFilteredRequests(prevRequests =>
        prevRequests.map(request =>
          request.id === requestId
            ? { ...request, status: newStatus }
            : request
        )
      );

      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      toast.success(`Refund request ${newStatus} successfully`);
      
      // Close modal if it's open
      if (showDetailsModal) {
        setShowDetailsModal(false);
      }
    } catch (error) {
      console.error('Error updating refund status:', error);
      toast.error(error.message || 'Failed to update refund status');
    }
  };

  const StatusUpdateButtons = ({ request }) => {
    if (request.status !== 'pending') {
      return null;
    }

    return (
      <div className="flex gap-2 mt-4">
        <button
          onClick={() => handleStatusChange(request.id, 'approved')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          <FiCheckCircle className="mr-2 -ml-1 h-4 w-4" />
          Approve Refund
        </button>
        
        <button
          onClick={() => handleStatusChange(request.id, 'rejected')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <FiX className="mr-2 -ml-1 h-4 w-4" />
          Reject Refund
        </button>
        
        <button
          onClick={() => handleStatusChange(request.id, 'processing')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <FiClock className="mr-2 -ml-1 h-4 w-4" />
          Mark as Processing
        </button>
      </div>
    );
  };

  const StatusBadge = ({ status }) => {
    const getStatusColor = (status) => {
      const colors = {
        approved: 'bg-green-100 text-green-800',
        rejected: 'bg-red-100 text-red-800',
        completed: 'bg-green-100 text-green-800',
        processing: 'bg-blue-100 text-blue-800'
      };
      return colors[status] || 'bg-gray-100 text-gray-800';
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  useEffect(() => {
    let result = [...requests];

    if (searchTerm) {
      result = result.filter(request =>
        request.order_details.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.order_details.items.some(item => 
          item.product_name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    if (filters.status !== 'all') {
      result = result.filter(request => request.status === filters.status);
    }

    if (filters.dateRange.start && filters.dateRange.end) {
      result = result.filter(request => {
        const requestDate = new Date(request.created_at);
        return requestDate >= filters.dateRange.start && 
               requestDate <= new Date(filters.dateRange.end.setHours(23, 59, 59, 999));
      });
    }

    setFilteredRequests(result);
  }, [requests, searchTerm, filters]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Refund Requests | Admin Dashboard</title>
      </Helmet>

      <div className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Refund Requests</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage customer refund requests and process payments
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex flex-col space-y-4">
            <div className="w-full">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by order ID or customer name..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <select
                className="border rounded-lg px-4 py-2 w-full"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="all">All Status</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="completed">Completed</option>
              </select>

              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative datepicker-icon-container w-full">
                  <FiCalendar className="datepicker-icon" />
                  <DatePicker
                    selected={filters.dateRange.start}
                    onChange={(date) => setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, start: date }
                    })}
                    selectsStart
                    startDate={filters.dateRange.start}
                    endDate={filters.dateRange.end}
                    dateFormat="MMM dd, yyyy"
                    placeholderText="Start Date"
                    className="w-full pl-10 pr-3 py-2 border rounded-lg"
                    wrapperClassName="w-full"
                    calendarClassName="shadow-lg"
                    showPopperArrow={false}
                    autoComplete="off"
                  />
                </div>
                <div className="relative datepicker-icon-container w-full">
                  <FiCalendar className="datepicker-icon" />
                  <DatePicker
                    selected={filters.dateRange.end}
                    onChange={(date) => setFilters({
                      ...filters,
                      dateRange: { ...filters.dateRange, end: date }
                    })}
                    selectsEnd
                    startDate={filters.dateRange.start}
                    endDate={filters.dateRange.end}
                    minDate={filters.dateRange.start}
                    dateFormat="MMM dd, yyyy"
                    placeholderText="End Date"
                    className="w-full pl-10 pr-3 py-2 border rounded-lg"
                    wrapperClassName="w-full"
                    calendarClassName="shadow-lg"
                    showPopperArrow={false}
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Requests List */}
        <div className="space-y-4">
          {filteredRequests.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-6 text-center">
              <p className="text-gray-500">No refund requests found</p>
            </div>
          ) : (
            filteredRequests.map(request => (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow-sm p-4 md:p-6"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Order #{request.order_details.order_number}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-sm w-fit ${
                        request.status === 'approved' ? 'bg-green-100 text-green-800' :
                        request.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-500">Customer</p>
                        <p className="font-medium">{request.customer_details.full_name}</p>
                        <p className="text-sm text-gray-500">{request.customer_details.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Amount</p>
                        <p className="font-medium text-indigo-600">
                          ₹{request.order_details.total_amount.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Reason</p>
                        <p className="font-medium">{request.reason}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Date</p>
                        <p className="font-medium">
                          {new Date(request.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-4">
                      <button
                        onClick={() => {
                          setSelectedRequest(request);
                          setShowDetailsModal(true);
                        }}
                        className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                      >
                        <FiEye className="w-4 h-4" />
                        View Details
                      </button>
                      {request.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleStatusChange(request.id, 'approved')}
                            className="text-sm text-green-600 hover:text-green-700 flex items-center gap-1"
                          >
                            <FiCheck className="w-4 h-4" />
                            Approve
                          </button>
                          <button
                            onClick={() => handleStatusChange(request.id, 'rejected')}
                            className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                          >
                            <FiX className="w-4 h-4" />
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Empty state if no requests */}
        {filteredRequests.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <FiDollarSign className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">No refund requests</h3>
            <p className="mt-1 text-sm text-gray-500">No refund requests match your current filters.</p>
          </div>
        )}
      </div>

      {/* Details Modal - Made responsive */}
      <AnimatePresence>
        {showDetailsModal && selectedRequest && (
          <RefundRequestDetails
            request={selectedRequest}
            onClose={() => setShowDetailsModal(false)}
            onRefresh={fetchRefundRequests}
          />
        )}
      </AnimatePresence>

      {/* Status Update Modal */}
      <StatusUpdateModal
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false);
          setSelectedRequest(null);
        }}
        onUpdate={handleStatusChange}
        currentStatus={selectedRequest?.status || 'pending'}
      />
    </>
  );
};

export default RefundRequests;  // Make sure this export is present




























